import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { apiContainer } from '@vlabs/api-bindings';
import type { LunaStreamsClient } from '@vlabs/api-bindings/src/luna-streams';

import type { TLunaStreamGroup, TLunaStream, TUiStreamGroup } from '../types';
import { buildUiLunaStreamsGroups } from '../utils/buildUiLunaStreamsGroups';

const REFETCH_DEFAULT_TIME = 5 * 60 * 1000;

interface TUseLunaStreamsUiGroupsResult {
  groups: TLunaStreamGroup[];
  streams: TLunaStream[];
  uiGroups: TUiStreamGroup[];
}

const EMPTY_GROUPS: TLunaStreamGroup[] = [];
const EMPTY_STREAMS: TLunaStream[] = [];

export function useLunaStreamsUiGroups(): TUseLunaStreamsUiGroupsResult {
  const lunaStreamsClient = apiContainer.lunaStreamsClient as LunaStreamsClient;

  const groupsQuery = useQuery<TLunaStreamGroup[]>({
    queryKey: ['luna-streams', 'groups'],
    queryFn: async () => lunaStreamsClient.groups.getAll(),
    staleTime: REFETCH_DEFAULT_TIME,
  });

  const streamsQuery = useQuery<TLunaStream[]>({
    queryKey: ['luna-streams', 'streams'],
    queryFn: async () => lunaStreamsClient.streams.getAll(),
    staleTime: REFETCH_DEFAULT_TIME,
  });

  const groups = groupsQuery.data ?? [];
  const streams = streamsQuery.data ?? [];

  const uiGroups = useMemo<TUiStreamGroup[]>(() => {
    return buildUiLunaStreamsGroups({
      groups: groupsQuery.data ?? EMPTY_GROUPS,
      streams: streamsQuery.data ?? EMPTY_STREAMS,
    });
  }, [groupsQuery.data, streamsQuery.data]);

  return { groups, streams, uiGroups };
}
